import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";

function ymNow() { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`; }
function nextMonth(ym, delta) {
  const [y, m] = ym.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth()+1).padStart(2,"0")}`;
}

export default function CalendarPage() {
  const [month, setMonth] = useState(ymNow());
  const [events, setEvents] = useState([]);
  const [annual, setAnnual] = useState([]);
  useEffect(() => { api.get("/calendar/events", { params: { month } }).then((r)=>setEvents(r.data.events||[])); }, [month]);
  useEffect(() => { api.get("/calendar/annual").then((r)=>setAnnual(r.data)); }, []);

  const [y, m] = month.split("-").map(Number);
  const first = new Date(Date.UTC(y, m-1, 1));
  const daysInMonth = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const startDow = first.getUTCDay();
  const cells = [];
  for (let i=0;i<startDow;i++) cells.push(null);
  for (let d=1;d<=daysInMonth;d++) cells.push(d);

  const byDay = {};
  events.forEach((e)=>{ const day = new Date(e.date).getUTCDate(); (byDay[day] = byDay[day]||[]).push(e); });

  const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500">Schedule</div>
        <h1 className="font-heading text-3xl font-bold text-slate-900">Calendar</h1>
      </div>

      <Card className="border-slate-200"><CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="font-heading text-lg font-semibold">{first.toLocaleString("default", { month: "long", year: "numeric", timeZone: "UTC" })}</div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={()=>setMonth(nextMonth(month, -1))} data-testid="cal-prev"><ChevronLeft className="w-4 h-4"/></Button>
            <Button variant="outline" size="sm" onClick={()=>setMonth(ymNow())} data-testid="cal-today">Today</Button>
            <Button variant="outline" size="sm" onClick={()=>setMonth(nextMonth(month, 1))} data-testid="cal-next"><ChevronRight className="w-4 h-4"/></Button>
          </div>
        </div>
        <div className="grid grid-cols-7 text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1">
          {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d)=>(<div key={d} className="p-2">{d}</div>))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((d, i)=>(
            <div key={d ? `d-${month}-${d}` : `empty-${month}-${i}`} className={`min-h-[92px] border border-slate-100 rounded-md p-1 text-xs ${d?"bg-white":"bg-slate-50"}`}>
              {d && <div className="text-slate-500 font-medium">{d}</div>}
              {d && (byDay[d]||[]).slice(0,3).map((e)=>(
                <div key={e.id} className={`mt-1 truncate px-1 py-0.5 rounded ${e.is_regulatory?"bg-red-50 text-red-800":"bg-blue-50 text-blue-800"}`}>{e.title}</div>
              ))}
              {d && (byDay[d]||[]).length>3 && <div className="text-[10px] text-slate-500 mt-1">+{(byDay[d]||[]).length-3} more</div>}
            </div>
          ))}
        </div>
      </CardContent></Card>

      <Card className="border-slate-200"><CardContent className="p-4">
        <h3 className="font-heading text-lg font-semibold mb-3">Annual service calendar (from workbook)</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-[11px] uppercase tracking-widest text-slate-500">
              <th className="text-left p-2">Service</th>{MONTHS.map((m)=>(<th key={m} className="p-2 text-center">{m}</th>))}
            </tr></thead>
            <tbody>
              {annual.map((r)=>(<tr key={r.id} className="border-t border-slate-100">
                <td className="p-2 font-medium">{r.service}</td>
                {MONTHS.map((m)=>(<td key={m} className="p-2 text-center">{(r.months||[]).includes(m)?<span className="inline-block w-2 h-2 rounded-full bg-emerald-500"/>:""}</td>))}
              </tr>))}
            </tbody>
          </table>
        </div>
      </CardContent></Card>
    </div>
  );
}
